import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { getCategories } from "@/lib/api";
import { catLabel } from "@/lib/content";

const LINKS_AFTER = [
  { key: "nav.heritage", to: "/pages/notre-histoire" },
  { key: "nav.ethics", to: "/pages/notre-ethique" },
  { key: "nav.rituals", to: "/blog" },
  { key: "nav.contact", to: "/pages/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [overHero, setOverHero] = useState(true);
  const [categories, setCategories] = useState([]);
  const { count } = useCart();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    getCategories().then((c) => setCategories(Array.isArray(c) ? c : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setOverHero(window.scrollY < window.innerHeight - 100);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const transparent = isHome && overHero && !open;
  const close = () => setOpen(false);

  return (
    <nav
      className={`mn-nav ${scrolled ? "scrolled" : ""} ${transparent ? "transparent" : ""}`}
      aria-label="Navigation principale"
      data-testid="main-nav"
    >
      <Link to="/" className="nav-logo" data-testid="nav-logo">
        Nerolya <span>Cosmetic</span>
      </Link>

      <ul className={`nav-links ${open ? "open" : ""}`} data-testid="nav-links">
        <li>
          <NavLink to="/" end onClick={close} className={({ isActive }) => (isActive ? "active" : "")} data-testid="nav-link-home">
            {t("nav.home")}
          </NavLink>
        </li>
        <li className="nav-dd" data-testid="nav-shop-dd">
          <NavLink
            to="/collections/all"
            onClick={close}
            className={({ isActive }) => (isActive ? "active" : "")}
            data-testid="nav-link-shop"
          >
            {t("nav.shop")}
            <ChevronDown size={12} strokeWidth={1.75} className="nav-dd-caret" aria-hidden="true" />
          </NavLink>
          <ul className="nav-dd-menu" data-testid="nav-shop-menu">
            <li>
              <Link to="/collections/all" onClick={close} data-testid="nav-cat-tous">
                {t("nav.allProducts")}
              </Link>
            </li>
            {categories.map((c) => (
              <li key={c}>
                <Link
                  to={`/collections/all?categorie=${encodeURIComponent(c)}`}
                  onClick={close}
                  data-testid={`nav-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {catLabel(c, lang)}
                </Link>
              </li>
            ))}
          </ul>
        </li>
        {LINKS_AFTER.map((l) => (
          <li key={l.to}>
            <NavLink
              to={l.to}
              onClick={close}
              className={({ isActive }) => (isActive ? "active" : "")}
              data-testid={`nav-link-${l.key.split(".")[1]}`}
            >
              {t(l.key)}
            </NavLink>
          </li>
        ))}
      </ul>

      <div className="nav-right">
        <div className="lang-switch" data-testid="lang-switch">
          <button
            className={lang === "fr" ? "active" : ""}
            onClick={() => setLang("fr")}
            aria-label="Français"
            data-testid="lang-fr"
          >
            FR
          </button>
          <span className="sep">/</span>
          <button
            className={lang === "nl" ? "active" : ""}
            onClick={() => setLang("nl")}
            aria-label="Nederlands"
            data-testid="lang-nl"
          >
            NL
          </button>
        </div>

        <button
          className="nav-cart"
          onClick={() => navigate("/panier")}
          aria-label={`${t("nav.cart")}, ${count}`}
          data-testid="nav-cart-btn"
        >
          <ShoppingBag size={14} strokeWidth={1.5} />
          {t("nav.cart")}
          <span className="cart-count" data-testid="cart-count">{count}</span>
        </button>

        <button
          className="burger"
          aria-label="Menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          data-testid="burger-btn"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </nav>
  );
}
